<script setup lang="ts">
import Cookies from 'js-cookie'
import { ref, onMounted } from 'vue'
import { v4 as uuidv4 } from 'uuid'
import { TransitionChild, TransitionRoot } from '@headlessui/vue'
import { Link } from '@inertiajs/vue3'
import { nextTick } from 'vue'

enum CookieCategory {
    Necessary = 'necessary',
    Analytics = 'analytics',
    Targeting = 'targeting',
}

interface CookieConsent {
    categories: CookieCategory[]
    revision: number
    consent_date: string
    consent_uuid: string
    last_consent_update: string
}

const open = ref(false)
const consent = ref<CookieConsent | null>(null)

onMounted(() => {
    try {
        consent.value = JSON.parse(Cookies.get('calypso_cc') || '')
    } catch (error) {
        //
    }

    if (!consent.value) {
        nextTick(() => open.value = true)
    }
})

const accept = () => {
    const consentDate = new Date
    const consent: CookieConsent = {
        categories: [CookieCategory.Necessary, CookieCategory.Analytics, CookieCategory.Targeting],
        revision: 0,
        consent_date: consentDate.toISOString(),
        consent_uuid: uuidv4(),
        last_consent_update: consentDate.toISOString(),
    }

    Cookies.set('calypso_cc', JSON.stringify(consent), {
        expires: 182,
        sameSite: 'Lax',
        secure: window.location.protocol === "https:",
    })

    open.value = false
}
</script>

<template>
    <TransitionRoot as="template" :show="open">
        <div class="fixed inset-x-0 bottom-0 z-20 pointer-events-none sm:px-6 sm:pb-6">
            <TransitionChild as="template" enter="transition ease-linear duration-250" enter-from="transform opacity-0 scale-95" enter-to="transform opacity-100 scale-100" leave="transition ease-linear duration-250" leave-from="transform opacity-100 scale-100" leave-to="transform opacity-0 scale-95">
                <div
                    class="p-6 py-4 bg-gray-900 shadow-lg pointer-events-auto sm:max-w-sm rounded-t-xl sm:rounded-b-xl ring-1 ring-white/20">
                    <p class="text-base font-medium text-white">{{ $t('layout.cookie-consent.title') }}</p>
                    <!-- <p class="">{!! $t('layout.cookie-consent.description', ['link' => route('privacy-policy')]) !!}</p> -->

                    <i18n-t tag="p" class="mt-1 text-[0.8rem] text-gray-200" keypath="layout.cookie-consent.description" scope="global">
                        <Link class="font-semibold text-white hover:underline" :href="route('privacy-policy')">{{ $t('layout.cookie-consent.description-link') }}</Link>
                    </i18n-t>
                    <div class="flex items-center mt-4 gap-x-5">
                        <button type="button" class="px-3 py-2 text-sm font-semibold text-black bg-white rounded-md shadow-sm hover:bg-gray-100" @click="accept">{{ $t('layout.cookie-consent.button') }}</button>
                    </div>
                </div>
            </TransitionChild>
        </div>
    </TransitionRoot>
</template>
